/**
 * AdminBlinterestHomepageControlsCard — Editorial toggles for the
 * Blinterest homepage rails. Lets admins hide/show Featured Collections,
 * Shop the Moment, and Just Added site-wide without a redeploy.
 *
 * Reads from `/api/admin/settings`; writes back through the existing
 * PUT /api/admin/settings merge endpoint.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { API } from "@/App";

const TOGGLES = [
  {
    key: "blinterest_show_featured_collections",
    label: "Featured Collections",
    hint: "Curated editorial collections rail on /blinterest.",
  },
  {
    key: "blinterest_show_shop_the_moment",
    label: "Shop the Moment",
    hint: "Time-boxed shopping moments (drops, launches, events).",
  },
  {
    key: "blinterest_show_just_added",
    label: "Just Added",
    hint: "Newest products across the platform.",
  },
];

export default function AdminBlinterestHomepageControlsCard() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/settings`, { withCredentials: true });
        if (!cancelled) setSettings(r.data || {});
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Couldn't load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = async (key) => {
    const next = !settings[key];
    setSaving(key);
    try {
      const r = await axios.put(
        `${API}/admin/settings`,
        { [key]: next },
        { withCredentials: true },
      );
      setSettings(r.data || settings);
      toast.success(`${TOGGLES.find((t) => t.key === key)?.label}: ${next ? "shown" : "hidden"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="rounded-2xl border border-tbc-navy/10 bg-white p-5 shadow-sm" data-testid="admin-blinterest-editorial-card">
      <div className="mb-4">
        <h3 className="font-heading text-lg text-tbc-navy">Homepage Editorial Rails</h3>
        <p className="text-xs text-tbc-navy/60 mt-1">
          Flip a rail off to hide it site-wide on <span className="font-mono">/blinterest</span>. Takes effect on the next page load — no redeploy.
        </p>
      </div>

      <div className="divide-y divide-tbc-navy/5">
        {loading ? (
          <div className="py-6 flex items-center gap-2 text-tbc-navy/50 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading toggles…
          </div>
        ) : TOGGLES.map((t) => {
          const on = !!settings[t.key];
          const busy = saving === t.key;
          return (
            <div key={t.key} className="flex items-start justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="font-medium text-tbc-navy flex items-center gap-2">
                  {on ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-tbc-navy/40" />}
                  {t.label}
                </p>
                <p className="text-xs text-tbc-navy/60 mt-0.5">{t.hint}</p>
              </div>
              <button
                type="button"
                onClick={() => toggle(t.key)}
                disabled={busy}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 transition-colors ${
                  on ? "bg-tbc-navy border-tbc-navy" : "bg-white border-tbc-navy/30"
                } ${busy ? "opacity-60" : ""}`}
                data-testid={`editorial-toggle-${t.key}`}
                aria-pressed={on}
              >
                <span
                  className={`inline-block h-4 w-4 mt-[1px] rounded-full transition-transform ${
                    on ? "translate-x-5 bg-tbc-gold" : "translate-x-0.5 bg-tbc-navy/40"
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
